import { useRef, useState, type CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, textoUbicacion, type CambioPlanilla, type Importacion, type ResultadoSincronizacion, type ResultadoUbicaciones } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEsAdmin } from '../lib/sesion';
import { csvDeFilas, filaVacia, filasVacias, tieneAlgo, type ColumnaManual, type FilaManual } from '../lib/csvManual';
import { abrir, aparecer, CURVA, desplegar, fila as escalon, suave } from '../components/movimiento';
import { Cambia, Cargando, ChipBoton, Encabezado, EstadoChip, Icono, Kpi, Numero, PanelError, useConfirmar } from '../components/ui';
import {
  altoMinimo,
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  encabezadoTabla,
  filaTabla,
  miles,
  mono,
  nota,
  tarjeta,
} from '../theme';

/**
 * Las columnas de la carga a mano; ver `lib/csvManual.ts` por qué es un CSV.
 *
 * `articulo` se rotula «Código de material» porque es eso: el código del
 * producto, que es lo que identifica al material. El nombre corto va en
 * `descripcion`. `numero_de_parte` es aparte y es del **pallet**: el número con
 * el que vino esa mercadería en ese ingreso, que no siempre coincide con el
 * código del material y por eso se guarda por línea.
 */
const COLUMNAS: ColumnaManual[] = [
  { clave: 'pallet', rotulo: 'Pallet', ancho: 140, ejemplo: 'PLT-000001' },
  { clave: 'numero_de_parte', rotulo: 'N° de parte', ancho: 150, ejemplo: '1721423040' },
  { clave: 'articulo', rotulo: 'Nombre corto', ancho: 130, ejemplo: 'TUBE' },
  { clave: 'descripcion', rotulo: 'Descripción', ancho: 230, ejemplo: 'OUTER TUBE 65x82x389' },
  { clave: 'fifo', rotulo: 'FIFO', ancho: 110, ejemplo: 'FIFO 8' },
  { clave: 'stock_rdm', rotulo: 'STOCK RDM', ancho: 110, ejemplo: '350' },
  { clave: 'stock_zf', rotulo: 'STOCK ZF', ancho: 110, ejemplo: '0' },
  { clave: 'ubicacion', rotulo: 'Ubicación', ancho: 120, ejemplo: 'B2-07' },
  { clave: 'ingreso', rotulo: 'Ingreso', ancho: 120, ejemplo: '21/08/2026' },
  // Vacío es lo normal: se fracciona. Se escribe sólo en el pallet que sale
  // entero, que es la excepción.
  { clave: 'tipo', rotulo: 'Sale', ancho: 120, ejemplo: 'completo' },
];

const vaciar = () => filasVacias(COLUMNAS);

/** Una clave que no cambia aunque la fila cambie de lugar: con el índice, quitar una animaría a la última. */
let contador = 0;
const nuevaClave = () => `f${contador++}`;

/**
 * Cada paso (elegir archivo, vista previa, confirmar, cargado) entra desde la
 * derecha, como la página que sigue, y el anterior se va hacia la izquierda.
 */
const desdeLaDerecha = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40, transition: { duration: 0.2, ease: CURVA } },
  transition: suave,
};


const campoTabla = { ...campo, height: 36, padding: '0 12px', fontSize: 13 };

/**
 * Las tablas de solo lectura (filas con problemas, vista previa) son tablas de
 * verdad: tarjetas en el celular (`.wms-tarjetas`) y renglones de siempre desde
 * tablet. El relleno va en clases —no en el estilo— para que la versión de
 * tarjeta pueda pisarlo.
 */
const thTabla: CSSProperties = { ...encabezadoTabla, display: 'table-cell', padding: undefined };
const tdTabla: CSSProperties = { borderBottom: `1px solid ${c.borde}`, minWidth: 0 };
const claseTh = 'px-2 py-2.5 first:pl-4 last:pr-4';
const claseTd = 'px-2 py-3 first:pl-4 last:pr-4';

// -------------------------------------------------- planilla de retrabajo

/** «hace 3 min», para no mostrar el ISO crudo de la última corrida. */
function haceTiempo(iso: string): string {
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'recién';
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return `hace ${Math.round(horas / 24)} d`;
}

/** «3 pallets nuevos · 5 salidas · 1 cierre», o «sin cambios». */
function resumenCorrida(r: ResultadoSincronizacion): string {
  const partes: string[] = [];
  if (r.nuevos.length) partes.push(`${r.nuevos.length} pallet${r.nuevos.length === 1 ? '' : 's'} nuevo${r.nuevos.length === 1 ? '' : 's'}`);
  if (r.salidas.length) partes.push(`${r.salidas.length} salida${r.salidas.length === 1 ? '' : 's'}`);
  if (r.cierres.length) partes.push(`${r.cierres.length} cierre${r.cierres.length === 1 ? '' : 's'}`);
  if (r.correcciones.length)
    partes.push(`${r.correcciones.length} cambio${r.correcciones.length === 1 ? '' : 's'} de contenedor`);
  return partes.length ? partes.join(' · ') : 'sin cambios';
}

/** «PLT-000123 · 1721423040 · JZ20260522-01 · entra con 170 piezas». */
const lineaCambio = (cambio: CambioPlanilla) =>
  `${cambio.pallet ? `${cambio.pallet} · ` : ''}${cambio.parte} · ${cambio.contenedor} · ${cambio.detalle}`;

/** Una lista de cambios de la misma clase, con su título y su cuenta. */
function ListaCambios({ titulo, items }: { titulo: string; items: CambioPlanilla[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: c.texto3, marginBottom: 6 }}>
        {titulo} ({items.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map((cambio) => (
          <div key={cambio.clave} style={{ fontSize: 13, color: c.texto, fontFamily: mono }}>
            {lineaCambio(cambio)}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * La sincronización con la hoja «Retrabajo ZF» de Google Sheets: se
 * previsualiza y recién después se aplica, igual que la carga de inventario.
 * Sólo la ve un admin porque toca stock de todos.
 */
function PlanillaRetrabajo() {
  const estado = useApi(() => api.planilla(), [], VIVO.normal);
  const [resultado, setResultado] = useState<ResultadoSincronizacion | null>(null);
  const [errorSync, setErrorSync] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, dialogo] = useConfirmar();

  async function cambiarPausa(pausar: boolean) {
    if (pausar) {
      const ok = await confirmar({
        titulo: '¿Pausar la sincronización automática?',
        detalle:
          'El sistema deja de leer la planilla Retrabajo cada pocos minutos. Lo que ya entró no se toca, y podés seguir corriéndola a mano con «Ver qué cambiaría».',
        boton: 'Pausar',
      });
      if (!ok) return;
    }
    setOcupado(true);
    setErrorSync('');
    try {
      await api.pausarPlanilla(pausar);
      estado.recargar();
    } catch (err) {
      setErrorSync(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function verPrevia() {
    setOcupado(true);
    setErrorSync('');
    setResultado(null);
    try {
      setResultado(await api.previaPlanilla());
    } catch (err) {
      setErrorSync(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function aplicar() {
    setOcupado(true);
    setErrorSync('');
    try {
      setResultado(await api.sincronizarPlanilla());
      estado.recargar();
    } catch (err) {
      setErrorSync(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  if (estado.cargando) return <div style={{ ...tarjeta, padding: 20 }}><Cargando texto="Revisando la planilla…" alto={60} /></div>;
  if (estado.error) return <PanelError mensaje={estado.error} onReintentar={estado.recargar} />;
  const info = estado.datos;
  if (!info) return null;

  if (!info.configurada) {
    return (
      <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Planilla de Retrabajo</span>
        <div style={{ fontSize: 13.5, color: c.texto2, textWrap: 'pretty' }}>
          La sincronización con la planilla todavía no está activada. Pedile a sistemas que la active.
        </div>
      </div>
    );
  }

  const cambios = resultado
    ? resultado.nuevos.length + resultado.salidas.length + resultado.cierres.length + resultado.correcciones.length
    : 0;
  const hayCambios = cambios > 0;
  const nadaEnResultado = resultado !== null && cambios + resultado.revisar.length === 0;

  return (
    <motion.div {...aparecer} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Planilla de Retrabajo</div>
          <div style={{ marginTop: 3, fontSize: 13, color: c.texto2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {info.pausada ? (
              <>
                <EstadoChip estado="Pausada" tono="warn" />
                <span>
                  No se sincroniza sola
                  {info.pausadaPor ? ` desde que la pausó ${info.pausadaPor}` : ''}
                  {info.pausadaEn ? ` ${haceTiempo(info.pausadaEn)}` : ''}. Podés correrla a mano.
                </span>
              </>
            ) : (
              <span>Se sincroniza sola cada {info.cadaMinutos} minutos con la hoja «{info.hoja}».</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => void cambiarPausa(!info.pausada)}
            disabled={ocupado}
            style={{ ...(info.pausada ? botonPrimario : botonSecundario), opacity: ocupado ? 0.5 : 1 }}
          >
            <Icono nombre={info.pausada ? 'play_arrow' : 'pause'} size={18} />
            {info.pausada ? 'Reanudar sincronización' : 'Pausar sincronización'}
          </button>
          <button type="button" onClick={verPrevia} disabled={ocupado} style={{ ...botonSecundario, opacity: ocupado ? 0.5 : 1 }}>
            {ocupado ? 'Revisando…' : 'Ver qué cambiaría'}
          </button>
          {hayCambios && !resultado?.aplicado && (
            <button type="button" onClick={aplicar} disabled={ocupado} style={{ ...botonPrimario, opacity: ocupado ? 0.5 : 1 }}>
              {ocupado ? 'Aplicando…' : 'Aplicar'}
            </button>
          )}
        </div>
      </div>

      {info.ultima && (
        <div style={{ fontSize: 13, color: c.texto2, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Última corrida {haceTiempo(info.ultima.cuando)}: {resumenCorrida(info.ultima)}.</span>
          {info.ultima.revisar.length > 0 && <EstadoChip estado={`${info.ultima.revisar.length} para revisar`} tono="warn" />}
        </div>
      )}
      <AnimatePresence>
        {info.error && <PanelError key={info.error} mensaje={info.error} />}
        {errorSync && <PanelError key={errorSync} mensaje={errorSync} />}
      </AnimatePresence>

      <AnimatePresence>
      {resultado && (
        <motion.div key="resultado" {...desplegar}>
        <div style={{ ...nota, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: c.texto3, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            {resultado.aplicado ? 'Se aplicó' : 'Vista previa: todavía no se escribió nada'}
          </span>
          {nadaEnResultado ? (
            <span style={{ fontSize: 13, color: c.texto2 }}>La hoja está al día: no hay nada para cambiar.</span>
          ) : (
            <>
              <ListaCambios titulo="Entran" items={resultado.nuevos} />
              <ListaCambios titulo="Salidas que se descuentan" items={resultado.salidas} />
              <ListaCambios titulo="Salen del depósito" items={resultado.cierres} />
              <ListaCambios titulo="Cambian de contenedor" items={resultado.correcciones} />
              {resultado.revisar.length > 0 && (
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: c.ambar, marginBottom: 6 }}>
                    Para revisar ({resultado.revisar.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {resultado.revisar.map((linea, i) => (
                      <div key={i} style={{ fontSize: 13, color: c.texto2 }}>
                        {linea}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {resultado.sinCambios > 0 && (
                <span style={{ fontSize: 12.5, color: c.texto3 }}>{miles(resultado.sinCambios)} sin cambios.</span>
              )}
            </>
          )}
        </div>
        </motion.div>
      )}
      </AnimatePresence>

      <span style={{ fontSize: 12, color: c.texto3, textWrap: 'pretty' }}>
        Lo que ya se cargó no se duplica: cada pallet de la hoja se reconoce por contenedor, factura, parte, ingreso
        y piezas originales.
      </span>
      {dialogo}
    </motion.div>
  );
}

/**
 * Volver a poner los pallets en su lugar después de vaciar el stock y
 * reimportarlo, con el CSV que se bajó desde Mantenimiento antes de vaciar.
 * El completo conserva su código PLT; el de Retrabajo se renumera al
 * importar, así que se reconoce por lo que es y `antes` muestra el código
 * viejo cuando cambió. Sólo lo ve un admin porque toca stock ya ubicado.
 */
function RestaurarUbicaciones() {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<ResultadoUbicaciones | null>(null);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  function reiniciar() {
    setArchivo(null);
    setPrevia(null);
    setError('');
    if (entrada.current) entrada.current.value = '';
  }

  function elegir(f: File) {
    setArchivo(f);
    setPrevia(null);
    setError('');
  }

  async function verPrevia() {
    if (!archivo) return;
    setOcupado(true);
    setError('');
    setPrevia(null);
    try {
      setPrevia(await api.previaUbicaciones(archivo));
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  async function aplicar() {
    if (!archivo) return;
    setOcupado(true);
    setError('');
    try {
      setPrevia(await api.restaurarUbicaciones(archivo));
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  const asignaciones = previa?.asignar ?? [];
  const mostrar = asignaciones.slice(0, 20);
  const resto = asignaciones.length - mostrar.length;

  return (
    <motion.div {...aparecer} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Restaurar ubicaciones</div>
          <div style={{ marginTop: 3, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
            El CSV que bajaste desde Mantenimiento antes de vaciar el stock.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ ...botonSecundario, position: 'relative', minHeight: 44 }}>
            <Icono nombre="upload_file" size={19} />
            {archivo ? archivo.name : 'Elegir archivo'}
            <input
              ref={entrada}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) elegir(f);
              }}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            />
          </label>
          <button
            type="button"
            onClick={verPrevia}
            disabled={!archivo || ocupado}
            style={{ ...botonPrimario, opacity: !archivo || ocupado ? 0.5 : 1 }}
          >
            {ocupado ? 'Revisando…' : 'Ver qué se ubica'}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {error && <PanelError key={error} mensaje={error} />}
        {ocupado && !previa && (
          <motion.div key="leyendo" {...aparecer} style={{ fontSize: 13.5, color: c.texto2 }}>
            Leyendo el archivo…
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
      {previa && (
        <motion.div key="previa" {...desplegar}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <EstadoChip
              estado={previa.aplicado ? 'Aplicado' : 'Vista previa: todavía no se escribió nada'}
              tono={previa.aplicado ? 'ok' : 'warn'}
            />
            <span style={{ fontSize: 13, color: c.texto2 }}>
              <Numero valor={asignaciones.length} /> se ubican · <Numero valor={previa.yaEstan} /> ya estaban en su lugar
            </span>
          </div>

          {asignaciones.length > 0 && (
            <div style={{ ...nota, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {mostrar.map((a) => (
                <div key={a.pallet} style={{ fontSize: 13, color: c.texto, fontFamily: mono }}>
                  {a.pallet} · posición {textoUbicacion(a.posicion, a.nivel)}
                  {a.antes !== a.pallet && <span style={{ color: c.texto3 }}> (antes {a.antes})</span>}
                </div>
              ))}
              {resto > 0 && <span style={{ fontSize: 12.5, color: c.texto3 }}>y {miles(resto)} más</span>}
            </div>
          )}

          {previa.sinPareja.length > 0 && (
            <div style={aviso('warn')}>
              <strong>{previa.sinPareja.length} sin ubicar:</strong> no se pudieron reconocer sin duda.
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {previa.sinPareja.map((linea, i) => (
                  <span key={i} style={{ display: 'block', fontSize: 12.5 }}>
                    {linea}
                  </span>
                ))}
              </div>
            </div>
          )}

          {previa.problemas.length > 0 && (
            <div style={aviso('warn')}>
              <strong>{previa.problemas.length} filas con problemas:</strong>
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {previa.problemas.map((linea, i) => (
                  <span key={i} style={{ display: 'block', fontSize: 12.5 }}>
                    {linea}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!previa.aplicado && asignaciones.length > 0 && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={reiniciar} style={botonSecundario}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={aplicar}
                disabled={ocupado}
                style={{ ...botonPrimario, opacity: ocupado ? 0.5 : 1 }}
              >
                {ocupado ? 'Aplicando…' : `Aplicar (${miles(asignaciones.length)})`}
              </button>
            </div>
          )}
          {previa.aplicado && (
            <button type="button" onClick={reiniciar} style={{ ...botonSecundario, alignSelf: 'flex-start' }}>
              Restaurar otro archivo
            </button>
          )}
        </div>
        </motion.div>
      )}
      </AnimatePresence>

      <span style={{ fontSize: 12, color: c.texto3, textWrap: 'pretty' }}>
        Cada pallet se reconoce por factura, part number, ingreso y piezas; el que no se puede reconocer sin duda no
        se ubica.
      </span>
    </motion.div>
  );
}

/**
 * Carga inicial del inventario: la mercadería que ya está físicamente guardada
 * cuando el sistema arranca vacío. Se puede subir un CSV o tipear las filas a
 * mano; en los dos casos primero se previsualiza y recién después se confirma,
 * para no escribir nada a ciegas.
 */
export default function CargarInventario({ onListo }: { onListo: () => void }) {
  const esAdmin = useEsAdmin();
  const [modo, setModo] = useState<'archivo' | 'manual'>('archivo');
  const [filas, setFilas] = useState<FilaManual[]>(vaciar);
  // Una clave por fila, en el mismo orden: es lo que deja animar bien al quitar una del medio.
  const [claves, setClaves] = useState<string[]>(() => vaciar().map(nuevaClave));
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Importacion | null>(null);
  const [hecho, setHecho] = useState<Importacion | null>(null);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  /** La grilla en blanco: tres filas nuevas, con sus claves. */
  function reponerFilas() {
    setFilas(vaciar());
    setClaves(vaciar().map(nuevaClave));
  }

  /** Vuelve al punto de partida sin borrar lo tipeado: se corrige y se reintenta. */
  function reiniciar() {
    setArchivo(null);
    setPrevia(null);
    setHecho(null);
    setError('');
    if (entrada.current) entrada.current.value = '';
  }

  function cambiarModo(nuevo: 'archivo' | 'manual') {
    reiniciar();
    setModo(nuevo);
  }

  /** Tocar una celda invalida la previa: lo que se vio ya no es lo que se cargaría. */
  function editar(indice: number, clave: string, valor: string) {
    setFilas((prev) => prev.map((f, i) => (i === indice ? { ...f, [clave]: valor } : f)));
    setArchivo(null);
    setPrevia(null);
    setError('');
  }

  async function analizar(f: File) {
    setOcupado(true);
    setError('');
    setPrevia(null);
    setHecho(null);
    setArchivo(f);
    try {
      setPrevia(await api.importarInventario(f, false));
    } catch (err) {
      setError(mensajeDeError(err));
      setArchivo(null);
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar() {
    if (!archivo) return;
    setOcupado(true);
    setError('');
    try {
      setHecho(await api.importarInventario(archivo, true));
      setPrevia(null);
      reponerFilas();
      onListo();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setOcupado(false);
    }
  }

  const resumen = hecho?.resumen ?? previa?.resumen;
  const problemas = hecho?.problemas ?? previa?.problemas ?? [];
  const cargadas = filas.filter(tieneAlgo).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <Encabezado
        bajada="La mercadería que ya está guardada en el depósito. Se previsualiza antes de escribir nada."
      >
        <div className="wms-tira !justify-start">
          <ChipBoton grupo="inventario-modo" activo={modo === 'archivo'} onClick={() => cambiarModo('archivo')}>
            Desde un archivo
          </ChipBoton>
          <ChipBoton grupo="inventario-modo" activo={modo === 'manual'} onClick={() => cambiarModo('manual')}>
            A mano
          </ChipBoton>
        </div>
        {modo === 'archivo' && (
          <>
            <button type="button" onClick={() => void api.descargarPlantilla()} style={botonSecundario}>
              <Icono nombre="download" size={19} />
              Descargar plantilla
            </button>
            <label style={{ ...botonPrimario, position: 'relative', minHeight: 48 }}>
              <Icono nombre="upload_file" size={19} />
              {archivo ? 'Elegir otro archivo' : 'Elegir archivo'}
              <input
                ref={entrada}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void analizar(f);
                }}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              />
            </label>
          </>
        )}
      </Encabezado>

      {esAdmin && <PlanillaRetrabajo />}
      {esAdmin && <RestaurarUbicaciones />}

      <AnimatePresence>{error && <PanelError key={error} mensaje={error} />}</AnimatePresence>

      <AnimatePresence mode="wait" initial={false}>
      {modo === 'archivo' && !previa && !hecho && (
        <motion.div key="instrucciones" {...desdeLaDerecha} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Cómo tiene que venir el archivo</span>
          <div style={{ fontSize: 13.5, color: c.texto2, textWrap: 'pretty' }}>
            Un CSV con una fila por pallet. Desde Excel: <em>Archivo → Guardar como → CSV UTF-8</em>. Los
            encabezados pueden estar en cualquier orden.
          </div>
          <div style={{ ...nota, fontFamily: mono, fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: c.texto }}>
            {'pallet;numero_de_parte;articulo;descripcion;fifo;stock_rdm;stock_zf;ubicacion;nivel;ingreso;tipo;contenedor;factura\n'}
            {';1721423040;TUBE;OUTER TUBE 65x82x389;FIFO 8;350;0;B2-07;1;21/08/2026;;BBUU 480435-4;JZ20260522-01'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: c.texto2, display: 'grid', gap: 5 }}>
            <li>
              <strong>Obligatorias:</strong> número de parte y la cantidad, sea la columna{' '}
              <span style={{ fontFamily: mono }}>cantidad</span> o el corte{' '}
              <span style={{ fontFamily: mono }}>stock_rdm</span> / <span style={{ fontFamily: mono }}>stock_zf</span>.
            </li>
            <li>
              <strong>Pallet:</strong> si viene vacío, el sistema le pone el número al confirmar (PLT- y el que
              sigue).
            </li>
            <li>
              <strong>Contenedor y factura:</strong> el contenedor que no exista se crea, con esa factura; el
              pallet queda colgado de él y sus etiquetas salen con la factura. El documento de recepción y el lote se
              completan después en Datos de contenedores.
            </li>
            <li>
              <strong>STOCK RDM y STOCK ZF:</strong> el mismo pallet con el stock partido. Las piezas del pallet son
              la suma de los dos, y el corte queda guardado.
            </li>
            <li>
              <strong>Ubicación:</strong> sector, rack y posición, como <span style={{ fontFamily: mono }}>B2-07</span>.
              Podés incluir el nivel: <span style={{ fontFamily: mono }}>B2-07 N1</span>. Sin nivel, va al más bajo libre.{' '}
              <strong>Se puede dejar vacía:</strong> el pallet entra sin ubicar y se le asigna posición después, desde
              Ubicaciones. Lo que no entra es una ubicación escrita mal.
            </li>
            <li>
              <span style={{ fontFamily: mono }}>tipo</span> vacío es un pallet que se fracciona en capachos, que es lo
              normal. Poné <span style={{ fontFamily: mono }}>completo</span> en el que sale entero: ése no se abre en la
              mesa.
            </li>
            <li>
              <strong>Ingreso:</strong> la fecha real en que entró, para que la estadía se calcule bien. Si falta, se
              toma hoy.
            </li>
            <li>Los sectores, racks y posiciones que no existan se crean solos a partir de las ubicaciones.</li>
          </ul>
        </motion.div>
      )}

      {modo === 'manual' && !hecho && (
        <motion.div key="manual" {...desdeLaDerecha} style={{ ...tarjeta, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>Cargar a mano</span>
            <span style={{ fontSize: 12.5, color: c.texto3 }}>una fila por pallet</span>
          </div>
          <div className="wms-scroll-x">
            {/* A diferencia de las otras tablas, las columnas son de ancho fijo
                porque adentro van inputs: el ancho lo manda el contenido.
                En el celular cada fila es una tarjeta con sus campos rotulados
                de a dos, y el encabezado sobra. */}
            <div className="relative min-w-0 tablet:min-w-max">
              <div className="hidden tablet:flex" style={{ ...encabezadoTabla, display: undefined }}>
                {COLUMNAS.map((col) => (
                  <span key={col.clave} style={{ width: col.ancho, flex: 'none' }}>
                    {col.rotulo}
                  </span>
                ))}
                <span style={{ width: 44, flex: 'none' }} />
              </div>
              <AnimatePresence initial={false} mode="popLayout">
              {filas.map((fila, i) => (
                <motion.div
                  key={claves[i] ?? i}
                  // Nace en el botón «Agregar fila», que está justo abajo, y las de abajo se corren.
                  initial={{ opacity: 0, scaleY: 0.4, y: -8 }}
                  animate={{ opacity: 1, scaleY: 1, y: 0 }}
                  exit={{ opacity: 0, scaleY: 0.5, transition: { duration: 0.2, ease: CURVA } }}
                  layout="position"
                  transition={suave}
                  className="grid grid-cols-2 gap-3 tablet:flex tablet:items-center tablet:gap-4"
                  style={{ ...filaTabla, display: undefined, alignItems: undefined, gap: undefined, transformOrigin: 'top' }}
                >
                  {COLUMNAS.map((col) => (
                    <label
                      key={col.clave}
                      className={`flex flex-col gap-1 tablet:w-(--ancho) tablet:flex-none${col.clave === 'articulo' || col.clave === 'descripcion' ? ' col-span-2 tablet:col-auto' : ''}`}
                      style={{ '--ancho': `${col.ancho}px`, minWidth: 0 } as CSSProperties}
                    >
                      <span className="tablet:hidden" style={{ fontSize: 12.5, color: c.texto2 }}>
                        {col.rotulo}
                      </span>
                      <input
                        value={fila[col.clave] ?? ''}
                        placeholder={col.ejemplo}
                        inputMode={col.clave.startsWith('stock') ? 'numeric' : undefined}
                        onChange={(e) => editar(i, col.clave, e.target.value)}
                        style={campoTabla}
                      />
                    </label>
                  ))}
                  <button
                    type="button"
                    title="Quitar la fila"
                    aria-label={`Quitar la fila ${i + 1}`}
                    onClick={() => {
                      if (filas.length > 1) {
                        setFilas((prev) => prev.filter((_, j) => j !== i));
                        setClaves((prev) => prev.filter((_, j) => j !== i));
                      } else reponerFilas();
                    }}
                    className="col-span-2 justify-self-end tablet:col-auto"
                    style={{ ...botonSecundario, width: 44, minHeight: altoMinimo(36), padding: 0, borderRadius: 999, flex: 'none' }}
                  >
                    <Icono nombre="close" size={17} />
                  </button>
                </motion.div>
              ))}
              </AnimatePresence>
            </div>
          </div>
          <motion.div layout="position" transition={suave} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => {
                setFilas((prev) => [...prev, filaVacia(COLUMNAS)]);
                setClaves((prev) => [...prev, nuevaClave()]);
              }}
              style={{ ...botonSecundario, padding: '9px 16px' }}
            >
              <Icono nombre="add" size={18} />
              Agregar fila
            </button>
            <span style={{ flex: 1, minWidth: 120, fontSize: 13, color: c.texto3 }}>
              {cargadas === 0 ? (
                'Ninguna fila cargada todavía.'
              ) : (
                <>
                  <Numero valor={cargadas} /> filas cargadas.
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() => void analizar(csvDeFilas(COLUMNAS, filas, 'inventario-manual.csv'))}
              disabled={ocupado || cargadas === 0}
              style={{ ...botonPrimario, opacity: ocupado || cargadas === 0 ? 0.5 : 1 }}
            >
              <Cambia>{ocupado ? 'Revisando…' : 'Previsualizar'}</Cambia>
            </button>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      <AnimatePresence>
        {ocupado && !resumen && (
          <motion.div key="cargando" {...aparecer} style={{ ...tarjeta, padding: 20, fontSize: 13.5, color: c.texto2 }} className="wms-cargando">
            {modo === 'manual' ? 'Revisando las filas…' : 'Leyendo el archivo…'}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hecho && (
          <motion.div
            key="hecho"
            role="status"
            {...desdeLaDerecha}
            style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}
          >
            <Icono nombre="task_alt" size={24} color={c.verde} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Inventario cargado</div>
              <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>
                {miles(hecho.resumen.validos)} pallets en stock, en {hecho.resumen.posiciones} posiciones.
              </div>
            </div>
            <button type="button" onClick={reiniciar} style={{ ...botonSecundario, padding: '9px 16px' }}>
              {modo === 'manual' ? 'Cargar más pallets' : 'Cargar otro archivo'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {resumen && (
        // Dos columnas en el celular; desde tablet, la grilla de siempre.
        <div className="grid grid-cols-2 gap-3 tablet:grid-cols-[repeat(auto-fit,minmax(190px,1fr))] tablet:gap-4">
          <motion.div className="grid" {...escalon(0)}>
<Kpi rotulo="Pallets a cargar" valor={resumen.validos} pie={`de ${miles(resumen.filas)} filas`} color={c.verde} />
</motion.div>
          <motion.div className="grid" {...escalon(1)}>
<Kpi
            rotulo="Con problemas"
            valor={resumen.conProblemas}
            pie={resumen.conProblemas ? 'no se cargan' : 'ninguna'}
            color={resumen.conProblemas ? c.rojo : c.texto}
          />
</motion.div>
          <motion.div className="grid" {...escalon(2)}>
<Kpi
            rotulo="Materiales nuevos"
            valor={resumen.articulosNuevos}
            pie={
              resumen.nuevosSinNombre
                ? `${miles(resumen.nuevosSinNombre)} sin nombre`
                : resumen.articulosNuevos
                  ? 'se dan de alta solos'
                  : 'ya estaban todos'
            }
            color={resumen.articulosNuevos ? c.texto : c.verde}
            pieColor={resumen.nuevosSinNombre ? c.ambar : undefined}
          />
</motion.div>
          <motion.div className="grid" {...escalon(3)}>
<Kpi
            rotulo="Piezas"
            valor={resumen.piezas}
            pie={
              resumen.sinNumeroDeParte
                ? `${resumen.articulos} materiales · ${miles(resumen.sinNumeroDeParte)} sin n° de parte`
                : `${resumen.articulos} materiales`
            }
            pieColor={resumen.sinNumeroDeParte ? c.ambar : undefined}
          />
</motion.div>
          <motion.div className="grid" {...escalon(4)}>
<Kpi
            rotulo="Posiciones"
            valor={resumen.posiciones}
            pie={
              resumen.sinUbicar
                ? `${miles(resumen.sinUbicar)} sin ubicar`
                : resumen.sectores.length
                  ? `sectores ${resumen.sectores.join(', ')}`
                  : '—'
            }
            color={resumen.sinUbicar ? c.ambar : c.texto}
          />
</motion.div>
          {/* Los que salen enteros. Es tan definitivo como la posición y se
              pone desde una columna fácil de olvidar, así que se muestra
              aunque sea cero: ver «0» es la forma de darse cuenta de que la
              columna quedó vacía. */}
          <motion.div className="grid" {...escalon(5)}>
<Kpi
            rotulo="Salen enteros"
            valor={resumen.completos}
            pie={resumen.completos ? 'no se fraccionan' : 'todos se fraccionan'}
            color={c.texto}
          />
</motion.div>
        </div>
      )}

      <AnimatePresence>
        {previa && (
          <motion.div
            key="confirmar"
            {...desdeLaDerecha}
            style={{ ...tarjeta, padding: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}
          >
            <div style={{ flex: 1, minWidth: 240, fontSize: 13.5, color: c.texto2, textWrap: 'pretty' }}>
              {previa.resumen.validos > 0
                ? 'Todavía no se escribió nada. Revisá el resumen y confirmá para cargarlo.'
                : modo === 'manual'
                  ? 'Ninguna fila se puede cargar. Corregí lo que está marcado abajo y volvé a previsualizar.'
                  : 'Ninguna fila se puede cargar. Corregí el archivo y volvé a subirlo.'}
            </div>
            <button type="button" onClick={reiniciar} style={botonSecundario}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmar}
              disabled={ocupado || previa.resumen.validos === 0}
              style={{ ...botonPrimario, opacity: ocupado || previa.resumen.validos === 0 ? 0.5 : 1 }}
            >
              <Cambia>{ocupado ? 'Cargando…' : `Confirmar ${miles(previa.resumen.validos)} pallets`}</Cambia>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
      {problemas.length > 0 && (
        <motion.div key="problemas" {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>Filas con problemas</span>
            <EstadoChip estado={`${problemas.length} sin cargar`} tono="err" />
          </div>
          <div className="wms-scroll-x">
            <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, color: c.texto }}>
              <thead>
                <tr>
                  <th className={claseTh} style={{ ...thTabla, width: 70, textAlign: 'left' }}>Línea</th>
                  <th className={claseTh} style={{ ...thTabla, width: 150, textAlign: 'left' }}>Pallet</th>
                  <th className={claseTh} style={{ ...thTabla, minWidth: 220, textAlign: 'left' }}>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {problemas.map((p) => (
                  <tr key={`${p.linea}-${p.pallet}`} style={{ background: c.rojoFila }}>
                    <td className={claseTd} data-rotulo="Línea" style={{ ...tdTabla, fontVariantNumeric: 'tabular-nums', color: c.texto2 }}>
                      {p.linea}
                    </td>
                    <td className={`${claseTd} wms-principal`} style={{ ...tdTabla, fontFamily: mono, fontSize: 13 }}>
                      {p.pallet}
                    </td>
                    <td className={claseTd} data-rotulo="Motivo" style={{ ...tdTabla, color: c.texto2 }}>
                      {p.motivo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {previa && previa.muestra && previa.muestra.length > 0 && (
        <motion.div key="muestra" {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto, flex: 1 }}>Cómo va a quedar</span>
            <span style={{ fontSize: 12.5, color: c.texto3 }}>primeras {previa.muestra.length} filas</span>
          </div>
          <div className="wms-scroll-x">
            <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, color: c.texto }}>
              <thead>
                <tr>
                  {(
                    [
                      ['Pallet', 140, false],
                      ['Artículo', 120, false],
                      ['FIFO', 100, false],
                      ['STOCK RDM', 100, true],
                      ['STOCK ZF', 100, true],
                      ['Piezas', 90, true],
                      ['Ubicación', 120, false],
                      ['Sale', 90, false],
                    ] as const
                  ).map(([titulo, ancho, derecha]) => (
                    <th key={titulo} className={claseTh} style={{ ...thTabla, minWidth: ancho, textAlign: derecha ? 'right' : 'left' }}>
                      {titulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previa.muestra.map((m) => (
                  <tr key={m.linea}>
                    <td className={`${claseTd} wms-principal`} style={{ ...tdTabla, fontFamily: mono, fontSize: 13 }}>
                      {m.pallet}
                    </td>
                    <td className={claseTd} data-rotulo="Artículo" style={{ ...tdTabla, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                      {m.articulo}
                    </td>
                    <td className={claseTd} data-rotulo="FIFO" style={{ ...tdTabla, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                      {m.lote ?? '—'}
                    </td>
                    <td className={claseTd} data-rotulo="STOCK RDM" style={{ ...tdTabla, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.texto2 }}>
                      {m.stockRdm === null ? '—' : miles(m.stockRdm)}
                    </td>
                    <td className={claseTd} data-rotulo="STOCK ZF" style={{ ...tdTabla, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.texto2 }}>
                      {m.stockZf === null ? '—' : miles(m.stockZf)}
                    </td>
                    <td className={claseTd} data-rotulo="Piezas" style={{ ...tdTabla, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                      {m.tipo === 'completo' ? '—' : miles(m.piezas)}
                    </td>
                    <td className={claseTd} data-rotulo="Ubicación" style={{ ...tdTabla, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                      {m.posicion}
                      {m.nivel ? ` N${m.nivel}` : ''}
                    </td>
                    <td className={claseTd} data-rotulo="Sale" style={{ ...tdTabla, fontSize: 12.5, color: m.tipo === 'completo' ? c.texto : c.texto3 }}>
                      {m.tipo === 'completo' ? 'entero' : 'capachos'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}
